import { useState } from "react";
import clsx from "clsx";
import { ChevronDownIcon } from "lucide-react";

import { JobPauseConfirmationDialog } from "./JobPauseConfirmationDialog";
import { JobStatusTransitionConfirmationDialog } from "./JobStatusTransitionConfirmationDialog";
import { ConfirmationDialog, OptionalTooltip, Row } from "@/components";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useCreateJobPauseRequest } from "@/hooks/mutations/useCreateJobPauseRequest";
import { useUpdateJobPause } from "@/hooks/mutations/useUpdateJobPause";
import { useUpdateTargetJob } from "@/hooks/mutations/useUpdateTargetJob";
import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";
import { useSuccessToast } from "@/hooks/useSuccessToast";
import {
  JOB_PAUSE_AUTHORIZED_EMAILS,
  jobPauseTypeToLabel,
  jobPauseTypeToRequestedLabel,
  jobPauseTypeToRequestLabel,
  jobStatusOptions,
  jobStatusToDotColor,
  jobStatusToLabel,
} from "@/lib/constants";
import { jobs } from "@/lib/jobs";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { Job, JobPauseType, JobStatus } from "@/types";

export const JobStatusDropdown = ({
  className,
  dotClassName,
  job,
  setJob,
}: {
  className?: string;
  dotClassName?: string;
  job: Job;
  setJob: ({ job }: { job: Job }) => void;
}) => {
  /* Database CRUD Operations */
  // Update
  const {
    mutate: updateJobPause,
    isPending: isPausePending,
    isError: isPauseError,
  } = useUpdateJobPause();
  const {
    mutate: createJobPauseRequest,
    isPending: isRequestPending,
    isError: isRequestError,
  } = useCreateJobPauseRequest();
  const { mutate: updateTargetJob, isPending, isError } = useUpdateTargetJob();

  /* React Hooks */
  const currentUser = useGetCurrentUser();
  const showSuccessToast = useSuccessToast();
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [pauseDialogType, setPauseDialogType] = useState<JobPauseType | null>(
    null,
  );
  const [isResumeDialogOpen, setIsResumeDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<JobStatus>(job.status);

  /* Helpers */
  const canPause = JOB_PAUSE_AUTHORIZED_EMAILS.includes(
    currentUser?.email.toLowerCase() ?? "",
  );
  const isPaused = jobs.isDisplayPaused(job.status, job.pausedAt);
  const pendingRequest = job.pendingPauseRequest;
  const activationBlockedReason = jobs.activationBlockedReason(job);

  /* Handlers */
  const handleClick = (status: JobStatus) => {
    if (status === job.status) {
      return;
    }

    mixpanelUtils.trackButtonClick(
      "Status option selected",
      MIXPANEL_SCREENS.JOB,
      { value: status },
    );
    setSelectedStatus(status);
    setIsConfirmationDialogOpen(true);
  };

  const handleConfirm = () => {
    updateTargetJob(
      {
        request: {
          ...job,
          status: selectedStatus,
        },
        targetJobId: job.id,
      },
      {
        onSuccess: (data) => {
          // Update the job in the relevant query cache.
          setJob({ job: data });

          // Close the dialog.
          setIsConfirmationDialogOpen(false);
        },
      },
    );
  };

  const handlePauseClick = (pauseType: JobPauseType) => {
    // Re-selecting the type the job is already in is a no-op — guac rejects it.
    if (isPaused && jobs.displayPauseType(job.pauseType) === pauseType) {
      return;
    }
    if (!canPause && pauseRequestBlockedReason()) {
      return;
    }
    mixpanelUtils.trackButtonClick(
      "Status option selected",
      MIXPANEL_SCREENS.JOB,
      { value: pauseType },
    );
    setPauseDialogType(pauseType);
  };

  const handlePauseConfirm = (pauseReason: string) => {
    if (!pauseDialogType) {
      return;
    }
    if (!canPause) {
      createJobPauseRequest(
        { jobId: job.id, pauseReason, pauseType: pauseDialogType },
        {
          onSuccess: (data) => {
            setJob({ job: data });
            setPauseDialogType(null);
            showSuccessToast({
              description: `${jobPauseTypeToRequestedLabel[pauseDialogType]} — waiting on approval.`,
            });
          },
        },
      );
      return;
    }
    updateJobPause(
      { jobId: job.id, pause: true, pauseReason, pauseType: pauseDialogType },
      {
        onSuccess: (data) => {
          setJob({ job: data });
          setPauseDialogType(null);
        },
      },
    );
  };

  const handleResumeClick = () => {
    mixpanelUtils.trackButtonClick(
      "Status option selected",
      MIXPANEL_SCREENS.JOB,
      { value: "RESUMED" },
    );
    setIsResumeDialogOpen(true);
  };

  const handleResumeConfirm = () => {
    updateJobPause(
      { jobId: job.id, pause: false },
      {
        onSuccess: (data) => {
          setJob({ job: data });
          setIsResumeDialogOpen(false);
        },
      },
    );
  };

  /* Render */
  const renderStatusOptions = () => {
    const options =
      job.status === JobStatus.ACTIVE
        ? [
            {
              label: jobStatusToLabel[JobStatus.CLOSED],
              value: JobStatus.CLOSED,
            },
          ]
        : jobStatusOptions;

    return options.map((option) => {
      const blockedReason =
        option.value === JobStatus.ACTIVE ? activationBlockedReason : undefined;

      return (
        <OptionalTooltip
          key={option.value}
          text={blockedReason}
          trackingLabel="Blocked job activation tooltip"
        >
          <Row className="w-full">
            <DropdownMenuCheckboxItem
              className="flex-1"
              checked={job.status === option.value}
              disabled={!!blockedReason}
              onClick={(e) => {
                e.stopPropagation();
                handleClick(option.value);
              }}
            >
              {option.label}
            </DropdownMenuCheckboxItem>
          </Row>
        </OptionalTooltip>
      );
    });
  };

  // Paused and covered are both always on offer, so a paused job can switch
  // straight to covered without passing back through active. The resume item
  // only appears once the job is in one of them.
  const renderPauseOptions = () => {
    if (job.status !== JobStatus.ACTIVE) {
      return null;
    }
    if (!canPause) {
      return (
        <>
          {renderResumeOption()}
          {renderPauseRequestOptions()}
        </>
      );
    }
    return (
      <>
        {renderResumeOption()}
        {Object.values(JobPauseType).map((pauseType) => (
          <DropdownMenuCheckboxItem
            key={pauseType}
            checked={
              isPaused && jobs.displayPauseType(job.pauseType) === pauseType
            }
            onClick={(e) => {
              e.stopPropagation();
              handlePauseClick(pauseType);
            }}
          >
            {jobPauseTypeToLabel[pauseType]}
          </DropdownMenuCheckboxItem>
        ))}
      </>
    );
  };

  const pauseRequestBlockedReason = () => {
    if (isPaused) {
      return `This job is already ${jobs.pauseLabel(job.pauseType).toLowerCase()}. Corbin has to resume it before it can be requested again.`;
    }
    if (pendingRequest) {
      return `Waiting on Corbin — ${jobs.pauseRequestSummary(pendingRequest)}`;
    }
    return undefined;
  };

  // Marking a job active is open to everyone — the allowlist only governs who
  // parks one without asking.
  const renderResumeOption = () => {
    if (!isPaused) return null;
    return (
      <DropdownMenuCheckboxItem
        checked={false}
        onClick={(e) => {
          e.stopPropagation();
          handleResumeClick();
        }}
      >
        {jobStatusToLabel[JobStatus.ACTIVE]}
      </DropdownMenuCheckboxItem>
    );
  };

  // Everyone off the pause allowlist asks a manager instead.
  const renderPauseRequestOptions = () => {
    const blockedReason = pauseRequestBlockedReason();
    return Object.values(JobPauseType).map((pauseType) => (
      <OptionalTooltip
        key={pauseType}
        text={blockedReason}
        trackingLabel="Blocked pause request tooltip"
      >
        {/* Tooltip hangs off the wrapper, not the item: a disabled item is
            pointer-events-none and would never fire a hover. */}
        <Row className="w-full">
          <DropdownMenuCheckboxItem
            className="flex-1"
            checked={pendingRequest?.pauseType === pauseType}
            disabled={!!blockedReason}
            onClick={(e) => {
              e.stopPropagation();
              handlePauseClick(pauseType);
            }}
          >
            {jobPauseTypeToRequestLabel[pauseType]}
          </DropdownMenuCheckboxItem>
        </Row>
      </OptionalTooltip>
    ));
  };

  // An open request reads as its own state on the trigger: the job is still
  // active, but "requested" is what someone looking at it needs to know.
  const triggerDotColor = () => {
    if (isPaused) return jobs.pauseDotColor(job.pauseType);
    if (pendingRequest) return jobs.pauseDotColor(pendingRequest.pauseType);
    return jobStatusToDotColor[job.status];
  };

  const triggerLabel = () => {
    if (isPaused) return jobs.pauseLabel(job.pauseType);
    if (pendingRequest) {
      return jobPauseTypeToRequestedLabel[pendingRequest.pauseType];
    }
    return jobStatusToLabel[job.status];
  };

  const triggerTooltip = () => {
    if (isPaused) {
      return jobs.pausedTooltip(job.pausedAt, job.pauseReason, job.pauseType);
    }
    if (pendingRequest) return jobs.pauseRequestSummary(pendingRequest);
    return undefined;
  };

  return (
    <>
      <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
        <OptionalTooltip
          text={triggerTooltip()}
          trackingLabel="Paused job tooltip"
        >
          <DropdownMenuTrigger asChild>
            <Button
              className={clsx("rounded-full", className)}
              // Don't allow jobs to be reopened, as it will make for a confusing user experience on the worker app
              disabled={job.status === JobStatus.CLOSED}
              variant="outlineSecondary"
            >
              <div
                className={clsx(
                  dotClassName ?? "h-3 w-3",
                  "rounded-full",
                  triggerDotColor(),
                )}
              />
              {triggerLabel()}
              <ChevronDownIcon className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
        </OptionalTooltip>
        <DropdownMenuContent align="start">
          {renderStatusOptions()}
          {renderPauseOptions()}
        </DropdownMenuContent>
      </DropdownMenu>
      <JobStatusTransitionConfirmationDialog
        jobStatus={selectedStatus}
        onConfirm={handleConfirm}
        onOpenChange={setIsConfirmationDialogOpen}
        open={!isDropdownOpen && isConfirmationDialogOpen} // https://github.com/shadcn-ui/ui/issues/1912#issuecomment-2197220521
        preventContentClickPropagation
        preventOverlayClickCapturePropagation
        isLoading={isPending}
        isError={isError}
      />
      <JobPauseConfirmationDialog
        isRequest={!canPause}
        onConfirm={handlePauseConfirm}
        onOpenChange={(open) => {
          if (!open) {
            setPauseDialogType(null);
          }
        }}
        open={!isDropdownOpen && !!pauseDialogType}
        pauseType={pauseDialogType ?? JobPauseType.PAUSED}
        isLoading={canPause ? isPausePending : isRequestPending}
        isError={canPause ? isPauseError : isRequestError}
      />
      <ConfirmationDialog
        description={
          "All system outreach and sourcing - internal and external - will resume for this job." +
          (job.pauseReason
            ? `\n\nIt was ${jobs.pauseLabel(job.pauseType).toLowerCase()} because: "${job.pauseReason}"`
            : "")
        }
        onConfirm={handleResumeConfirm}
        onOpenChange={setIsResumeDialogOpen}
        open={!isDropdownOpen && isResumeDialogOpen}
        preventContentClickPropagation
        preventOverlayClickCapturePropagation
        title="Resume this job?"
        isLoading={isPausePending}
        isError={isPauseError}
      />
    </>
  );
};
